<template>
  <v-navigation-drawer
    :value="showFeaturedArticles"
    class="featured-articles-drawer"
    :class="[appName]"
    width="50vw"
    height="min(calc(100vh - 70px), 880px)"
    disable-route-watcher
    hide-overlay
    fixed
    right
  >
    <div class="content">
      <header>
        <div class="close-icon invisible"></div>
        <div class="slide-icons">
          <svg
            v-for="(featuredArticle, index) in featuredArticles"
            :key="`bullet-${index}`"
            class="slide-icon"
            viewBox="0 0 12 12"
            width="11"
            height="11"
            xmlns="http://www.w3.org/2000/svg"
            @click="goToIndex(index)"
          >
            <circle
              cx="6"
              cy="6"
              r="5"
              class="bullet"
              :class="{ selected: currentIndex === index }"
            />
          </svg>
        </div>
        <div class="close-icon" @click="closeFeaturedArticles">
          <BaseIcon icon="close" hover />
        </div>
      </header>
      <div class="slider">
        <div
          v-for="(featuredArticle, index) in featuredArticles"
          v-show="currentIndex === index"
          :key="`slide-${index}`"
          class="slide"
          :style="featuredArticleStyle(featuredArticle)"
          @click="visitArticle"
        >
          <div class="featured-content" :class="[appName]">
            <div
              class="previous"
              :class="{ disabled: isFirst }"
              @click.stop="goToIndex(currentIndex - 1)"
            >
              <BaseIcon icon="arrow-left" hover />
            </div>
            <div class="featured-wrapper">
              <div class="featured-category" :class="[appName]">
                {{ featuredArticle.article.featured_category }}
              </div>
              <div class="featured-title">
                {{ featuredArticle.article.featured_title }}
              </div>
              <div class="featured-description" :class="[appName]">
                {{ featuredArticle.article.featured_description }}
              </div>
            </div>
            <div
              class="next"
              :class="{ disabled: isLast }"
              @click.stop="goToIndex(currentIndex + 1)"
            >
              <BaseIcon icon="arrow-right" hover />
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";
import { appIdentifier } from "@/utils/appIdentifier.js";

export default {
  name: "FeaturedArticlesDrawer",
  data: () => {
    return {
      currentIndex: 0
    };
  },
  computed: {
    ...mapState({
      locale: state => state.locale,
      featuredArticles: state => state.featuredArticles,
      showFeaturedArticles: state => state.showFeaturedArticles
    }),
    appName() {
      return appIdentifier();
    },
    isFirst() {
      return this.currentIndex === 0;
    },
    isLast() {
      return this.currentIndex === this.featuredArticles.length - 1;
    }
  },
  watch: {
    locale: function() {
      this.getFeaturedArticles();
    }
  },
  created() {
    this.getFeaturedArticles();
  },
  methods: {
    getFeaturedArticles() {
      this.$store.dispatch(this.$actions.GET_FEATURED_ARTICLES);
    },
    goToIndex(index) {
      if (index < 0 || index >= this.featuredArticles.length) {
        return;
      }

      this.currentIndex = index;
    },
    closeFeaturedArticles() {
      this.$store.commit(this.$mutations.SHOW_FEATURED_ARTICLES, false);
    },
    visitArticle() {
      this.closeFeaturedArticles();

      this.$router.push({
        name: "article-share",
        params: {
          id: this.featuredArticles[this.currentIndex].article_id
        }
      });
    },
    featuredArticleStyle(featuredArticle) {
      if (featuredArticle.article.featured_image?.image_url) {
        return {
          backgroundImage: `url(${featuredArticle.article.featured_image?.image_url})`
        };
      } else {
        return {
          backgroundImage: `url(${require("@/assets/svg/featured-article-placeholder.svg")})`,
          backgroundSize: "auto"
        };
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.featured-articles-drawer {
  border-top-left-radius: 80px;
  top: auto !important;
  bottom: 0;

  &.huygens {
    background-color: rgba($primary-huygens, 0.9);
  }

  &.dordrecht5D {
    background-color: rgba($primary-dordrecht5D, 0.9);
  }

  &.canals5D {
    background-color: rgba($primary-canals5D, 0.9);
  }
}

.content {
  display: flex;
  flex-direction: column;
  height: 100%;

  header {
    flex: 0 0 48px;
  }

  .slider {
    flex: 1 1 auto;
    position: relative;
  }
}

header {
  display: flex;
  width: 100%;
  align-items: center;

  .slide-icons {
    flex: 1 1 auto;
  }

  .close-icon {
    flex: 0 0 48px;
  }
}

.bullet {
  color: #ffffff80;
  fill: #ffffff80;

  &.selected {
    color: #e3b2b2;
    fill: #e3b2b2;
  }
}

.slide-icons {
  display: flex;
  justify-content: center;
}

.slide-icon {
  margin: 0px 3px;

  &:hover {
    cursor: pointer;
  }
}

.close-icon {
  display: flex;
  justify-content: center;
}

.slide {
  cursor: pointer;
  height: 100%;
  width: 100%;
  border-top-left-radius: 80px;
  background-size: cover;
  background-position: center center;
  position: absolute;
}

.featured-content {
  border-top-right-radius: 28px;
  padding: 30px 0 35px 0;
  position: absolute;
  display: flex;
  width: 75%;
  bottom: 0;
  left: 0;

  &.huygens {
    background-color: rgba($primary-huygens, 0.8);
  }

  &.dordrecht5D {
    background-color: rgba($primary-dordrecht5D, 0.8);
  }

  &.canals5D {
    background-color: rgba($primary-canals5D, 0.8);
  }
}

.previous,
.next {
  cursor: pointer;
  display: flex;
  align-items: flex-end;
  padding: 0 25px;

  &.disabled {
    visibility: hidden;
  }
}

.featured-wrapper {
  font-family: "AWConquerorCarved-One";
  flex-grow: 1;

  .featured-category {
    margin-bottom: 8px;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 16px;

    &.huygens {
      color: #e07a5f;
      font-family: "Hurme Geometric Sans 1" !important;
      text-transform: uppercase;
      font-weight: 700;
    }

    &.dordrecht5D {
      color: #f4a896;
    }

    &.canals5D {
      color: #213e52;
    }
  }

  .featured-title {
    margin-bottom: 2px;
    font-size: 31px;
    letter-spacing: 4.65px;
    line-height: 35px;
    color: white;
  }

  .featured-description {
    font-size: 15px;
    letter-spacing: 4.65px;
    line-height: 18px;
    color: white;

    &.huygens {
      font-family: "Hurme Geometric Sans 1" !important;
      text-transform: uppercase;
    }
  }
}
</style>
